// «Accesorios» (paso 05): la ancla, los extras agrupados por SKU, el catálogo de extras y el resumen con
// los montos del servidor. Puro, sin React.
import { shownDiscountCode } from '@/shared/patterns/SalePromotion/discountCode';
import { categoryOf, promotionRefsOf, type LinePromotionRef } from './cartLines';
import { isService } from './catalogAvailability';
import { isAccessoryKit, phoneLineOf } from './productSuggestions';
import { catalogDisplayName } from '@/shared/lib/format/itemDisplayName';
import { variantLabel } from '@/shared/lib/format/capacity';
import type { CatalogItem, ReservedLine, WizardSession } from './types';

/** Categoría del catálogo de extras; un servicio va aparte aunque su categoría sea accesorio. */
export type ExtraCategory = 'ALL' | 'ACCESSORY' | 'SIM' | 'MODEM' | 'SERVICE';

/** Pestañas del catálogo de extras, en el orden del mock. */
export const EXTRA_CATEGORY_FILTERS: { value: ExtraCategory; label: string }[] = [
    { value: 'ALL', label: 'Todo' },
    { value: 'ACCESSORY', label: 'Accesorios' },
    { value: 'SIM', label: 'SIM' },
    { value: 'MODEM', label: 'Módems' },
    { value: 'SERVICE', label: 'Servicios' },
];

const NO_DESCRIPTIONS = new Map<string, string>();

/** Tope de extras a crédito: este porcentaje del neto de la ancla, hacia abajo. */
const EXTRAS_CAP_PCT = 30;

export function extraCategoryOf(item: Pick<CatalogItem, 'category' | 'itemType'>): ExtraCategory {
    if (isService(item)) return 'SERVICE';
    const category = categoryOf(item);
    return category === 'SIM' || category === 'MODEM' ? category : 'ACCESSORY';
}

const isExtraItem = (item: CatalogItem, items: readonly CatalogItem[]): boolean => {
    const category = categoryOf(item);
    if (category === 'BUNDLE') return isAccessoryKit(item, items);
    return isService(item) || category !== 'PHONE';
};

/** Extras del catálogo (sin teléfonos ni kits con teléfono), filtrados por nombre o SKU y en orden alfabético. */
export function extraOffersOf(
    items: CatalogItem[],
    category: ExtraCategory,
    query: string,
): CatalogItem[] {
    const q = query.trim().toLowerCase();
    return items
        .filter((i) => isExtraItem(i, items))
        .filter((i) => category === 'ALL' || extraCategoryOf(i) === category)
        .filter(
            (i) =>
                q === '' ||
                i.sku.toLowerCase().includes(q) ||
                catalogDisplayName(i, NO_DESCRIPTIONS).toLowerCase().includes(q),
        )
        .sort((a, b) =>
            catalogDisplayName(a, NO_DESCRIPTIONS).localeCompare(
                catalogDisplayName(b, NO_DESCRIPTIONS),
                'es-MX',
            ),
        );
}

const liveLines = (session: WizardSession): ReservedLine[] =>
    (session.reservedLines ?? []).filter((l) => l.status !== 'RELEASING');

/** Línea ancla: la que fija el servidor o, sin ella, la del teléfono o kit. */
export function anchorLineOf(session: WizardSession): ReservedLine | undefined {
    const lines = liveLines(session);
    return lines.find((l) => l.lineId === session.financedLineId) ?? phoneLineOf(session);
}

/** La ancla existe y SureKeep no la financia: flag de la línea y, si hay reparto, ancla en 0. */
export function isAnchorUnfinanceable(session: WizardSession): boolean {
    const anchor = anchorLineOf(session);
    if (anchor === undefined || anchor.financeable) return false;
    const inPreview = session.financingPreview?.lines.find((l) => l.lineId === anchor.lineId);
    return (inPreview?.financedCents ?? 0) <= 0;
}

/** Líneas de extras vivas: ni la ancla ni los regalos. */
/** Líneas de extras de la venta: ni la ancla ni sus regalos. */
export function extraLinesOf(session: WizardSession): ReservedLine[] {
    const anchorId = anchorLineOf(session)?.lineId;
    return liveLines(session).filter((l) => l.lineId !== anchorId && l.role !== 'GIFT');
}

/** Piezas en la venta por SKU de extra. */
export function extraCountsBySku(session: WizardSession): Map<string, number> {
    const counts = new Map<string, number>();
    for (const l of extraLinesOf(session)) counts.set(l.sku, (counts.get(l.sku) ?? 0) + l.qty);
    return counts;
}

/** Todas las líneas vivas de un extra, que es lo que suelta «✕». */
export function extraLinesOfSku(session: WizardSession, sku: string): ReservedLine[] {
    return extraLinesOf(session).filter((l) => l.sku === sku);
}

/**
 * Kits que traen el teléfono de la ancla; una ancla que ya es kit no tiene. El catálogo del wizard
 * ya quitó los kits sin piezas contando lo apartado en la sesión: `stock` no se vuelve a filtrar.
 */
export function kitSwapsFor(items: CatalogItem[], anchor: ReservedLine | undefined): CatalogItem[] {
    if (anchor === undefined || anchor.components.length > 0) return [];
    return items
        .filter(
            (kit) =>
                categoryOf(kit) === 'BUNDLE' &&
                (kit.components ?? []).some((c) => c.sku === anchor.sku),
        )
        .sort((a, b) =>
            catalogDisplayName(a, NO_DESCRIPTIONS).localeCompare(
                catalogDisplayName(b, NO_DESCRIPTIONS),
                'es-MX',
            ),
        );
}

/** La línea más reciente de un SKU, que es la que suelta «−». */
export function latestExtraLineOf(session: WizardSession, sku: string): ReservedLine | undefined {
    return extraLinesOf(session)
        .filter((l) => l.sku === sku)
        .reduce<ReservedLine | undefined>(
            (latest, l) => (latest === undefined || l.expiresAt >= latest.expiresAt ? l : latest),
            undefined,
        );
}

const netOf = (l: ReservedLine): number =>
    Math.max(0, (l.basePriceCents ?? 0) * l.qty - (l.discountCents ?? 0));

export interface SaleSummary {
    equipment?: {
        line: ReservedLine;
        name: string;
        isKit: boolean;
        baseCents: number;
        netCents: number;
        discountCode?: string;
    };
    gifts: { key: string; sku: string; name: string; valueCents: number }[];
    /** `financeable: false` si SureKeep no financia alguna pieza del SKU. */
    extras: {
        sku: string;
        name: string;
        qty: number;
        totalCents: number;
        financeable: boolean;
        /** Antes de promos; ausente en resúmenes viejos. */
        baseCents?: number;
        /** Promo que bajó el extra (la primera línea con descuento). */
        promotionId?: string;
        /** Todas las promos que bajaron el extra, sin repetir. */
        promotions?: LinePromotionRef[];
        promotionVersion?: number;
        discountCode?: string;
    }[];
    totalCents: number;
    /** Solo con el reparto prendido (financingPreview). */
    credit?: { financedCents: number; capCents: number; cashCents: number };
}

/** Promos de varias líneas, sin repetir y en el orden en que aparecen. */
function mergeRefs(
    a: readonly LinePromotionRef[],
    b: readonly LinePromotionRef[],
): LinePromotionRef[] {
    const out = [...a];
    for (const ref of b) if (!out.some((r) => r.promotionId === ref.promotionId)) out.push(ref);
    return out;
}

/** Resumen de «Accesorios» con los precios netos y el reparto que manda el servidor. */
export function saleSummaryOf(
    session: WizardSession,
    descriptions: ReadonlyMap<string, string> = NO_DESCRIPTIONS,
): SaleSummary {
    const nameOf = (l: ReservedLine): string =>
        catalogDisplayName({ sku: l.sku, ...l.product }, descriptions);
    const anchor = anchorLineOf(session);
    const gifts = liveLines(session)
        .filter((l) => l.role === 'GIFT' && l.linkedLineId === anchor?.lineId)
        .map((l) => ({
            key: l.lineId,
            sku: l.sku,
            name: nameOf(l),
            valueCents: l.originalPriceCents ?? 0,
        }));

    const extraLines = extraLinesOf(session);
    const bySku = new Map<string, SaleSummary['extras'][number]>();
    for (const l of extraLines) {
        const row = bySku.get(l.sku) ?? {
            sku: l.sku,
            name: nameOf(l),
            qty: 0,
            totalCents: 0,
            financeable: true,
        };
        const discounted = (l.discountCents ?? 0) > 0;
        bySku.set(l.sku, {
            ...row,
            qty: row.qty + l.qty,
            totalCents: row.totalCents + netOf(l),
            baseCents: (row.baseCents ?? 0) + (l.basePriceCents ?? 0) * l.qty,
            financeable: row.financeable && l.financeable,
            ...(discounted && {
                promotions: mergeRefs(row.promotions ?? [], promotionRefsOf(l)),
            }),
            ...(discounted &&
                row.promotionId === undefined &&
                l.promotionId && {
                    promotionId: l.promotionId,
                    ...(l.promotionVersion !== undefined && {
                        promotionVersion: l.promotionVersion,
                    }),
                }),
            ...(discounted &&
                row.discountCode === undefined &&
                l.discountCode && {
                    discountCode: l.discountCode,
                }),
        });
    }
    const extras = [...bySku.values()];

    const equipment =
        anchor === undefined
            ? undefined
            : {
                  line: anchor,
                  name: nameOf(anchor),
                  isKit: anchor.components.length > 0,
                  baseCents: (anchor.basePriceCents ?? 0) * anchor.qty,
                  netCents: netOf(anchor),
                  discountCode: shownDiscountCode(anchor),
              };
    const totalCents =
        (equipment?.netCents ?? 0) + extras.reduce((sum, e) => sum + e.totalCents, 0);

    const preview = session.financingPreview;
    let credit: SaleSummary['credit'];
    if (preview !== undefined && !isAnchorUnfinanceable(session)) {
        const financed = new Map(preview.lines.map((l) => [l.lineId, l.financedCents]));
        const reserved = extraLines.filter((l) => l.status === 'RESERVED');
        const financedCents = reserved.reduce((sum, l) => sum + (financed.get(l.lineId) ?? 0), 0);
        const cashCents = reserved.reduce(
            (sum, l) => sum + Math.max(0, netOf(l) - (financed.get(l.lineId) ?? 0)),
            0,
        );
        credit = { financedCents, capCents: preview.capCents, cashCents };
    }

    return { equipment, gifts, extras, totalCents, credit };
}

/** Cómo cambia el tope de extras al cambiar la ancla por un kit. */
export interface KitSwapCapChange {
    fromCents: number;
    toCents: number;
    /** Lo que de los extras financiables deja de caber y pasa a contado. */
    toCashCents: number;
}

/** Contado de los extras con un tope: llenan el tope en orden de alta y el que lo cruza se parte. */
function extrasCashFor(extraNets: number[], capCents: number): number {
    let left = capCents;
    let cash = 0;
    for (const net of extraNets) {
        const financed = Math.min(net, left);
        left -= financed;
        cash += net - financed;
    }
    return cash;
}

/** Tope de hoy contra el del kit con las líneas actuales; `undefined` sin reparto. */
export function kitSwapCapChange(
    session: WizardSession,
    kitPriceCents: number,
): KitSwapCapChange | undefined {
    const preview = session.financingPreview;
    if (preview === undefined) return undefined;
    const toCents = Math.floor((kitPriceCents * EXTRAS_CAP_PCT) / 100);
    const extraNets = extraLinesOf(session)
        .filter((l) => l.status === 'RESERVED' && l.financeable)
        .map(netOf);
    const toCashCents = Math.max(
        0,
        extrasCashFor(extraNets, toCents) - extrasCashFor(extraNets, preview.capCents),
    );
    return { fromCents: preview.capCents, toCents, toCashCents };
}

/** Reparto de la venta para «Financiable / De contado»; todo con montos del servidor. */
export interface FinancingSplit {
    /** Lo que SureKeep financia de las líneas apartadas, ancla incluida. */
    financedCents: number;
    /** Total de la venta (el mismo del resumen). */
    totalCents: number;
    /** Total menos lo financiable; el enganche no se resta aquí. */
    cashCents: number;
}

/** Suma `financedCents` de `financingPreview.lines` de las líneas RESERVED; `undefined` sin reparto. */
export function financingSplitOf(session: WizardSession): FinancingSplit | undefined {
    const preview = session.financingPreview;
    if (preview === undefined) return undefined;
    const reserved = new Set(
        liveLines(session)
            .filter((l) => l.status === 'RESERVED')
            .map((l) => l.lineId),
    );
    const financedCents = preview.lines
        .filter((l) => reserved.has(l.lineId))
        .reduce((sum, l) => sum + Math.max(0, l.financedCents), 0);
    const { totalCents } = saleSummaryOf(session);
    return {
        financedCents: Math.min(financedCents, totalCents),
        totalCents,
        cashCents: Math.max(0, totalCents - financedCents),
    };
}

/** Variante de cada SKU de la venta («256 GB · Negro», «Rojo»); sin variante no hay entrada. */
export function lineVariantsBySku(session: WizardSession): Map<string, string> {
    const variants = new Map<string, string>();
    for (const l of liveLines(session)) {
        const label = variantLabel(l.product ?? {});
        if (label !== '') variants.set(l.sku, label);
    }
    return variants;
}

/** Piezas del kit que no son el teléfono, con su nombre del catálogo (o el SKU) y «× N» si van varias. */
export function kitPieceNamesOf(
    line: ReservedLine,
    items: readonly CatalogItem[],
    descriptions: ReadonlyMap<string, string> = NO_DESCRIPTIONS,
): string[] {
    const bySku = new Map(items.map((i) => [i.sku, i]));
    return line.components
        .filter((c) => c.category !== 'PHONE')
        .map((c) => {
            const item = bySku.get(c.sku);
            const name = item === undefined ? c.sku : catalogDisplayName(item, descriptions);
            return c.qty > 1 ? `${name} × ${String(c.qty)}` : name;
        });
}
